import { useState } from 'react';
import { useWalletStatement } from '@/hooks/wallet';
import { WalletTransactionTable } from '@/components/wallet/WalletTransactionTable';
import { StatementFilters } from '@/components/wallet/StatementFilters';
import { Skeleton } from '@/components/ui/Skeleton';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks';

export function WalletStatementPage() {
  const location = useLocation();
  const { user } = useAuth();
  const backPath = location.pathname.startsWith('/medico') || user?.role === 'DOCTOR' ? '/medico/carteira' : '/carteira';

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading } = useWalletStatement(page);
  const transactions = data?.items ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / 20);

  const filteredTransactions = transactions.filter((tx) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchDesc = (tx.description ?? '').toLowerCase().includes(term);
      const matchType = tx.type.toLowerCase().includes(term);
      if (!matchDesc && !matchType) return false;
    }
    if (dateFrom && new Date(tx.createdAt) < new Date(dateFrom)) return false;
    if (dateTo) {
      const endDate = new Date(dateTo);
      endDate.setHours(23, 59, 59, 999);
      if (new Date(tx.createdAt) > endDate) return false;
    }
    return true;
  });

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <Link
          to={backPath}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium mb-2 inline-block"
        >
          &larr; Voltar para Carteira
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Extrato Financeiro</h1>
        <p className="text-sm text-gray-500 mt-1">Histórico de transações da carteira</p>
      </div>

      <StatementFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        dateFrom={dateFrom}
        onDateFromChange={setDateFrom}
        dateTo={dateTo}
        onDateToChange={setDateTo}
      />

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Transações</h2>
        </div>
        <div className="p-6">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 rounded-lg" />
              ))}
            </div>
          ) : (
            <WalletTransactionTable transactions={filteredTransactions} />
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Página {page} de {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
              aria-label="Página anterior"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
              aria-label="Próxima página"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
